import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import exceljs from "exceljs";
import { saveAs } from "file-saver";
//-------------------------------------------
import Resize from "src/components/common/resize";
import magnifierImg from "src/img/magnifier.png";
import Footer from "src/components/common/frame/footer";
import { useMyStore } from "src/components/main/mainStore";
import {
    arrayReducer, 
    myAxios, 
    MyFormData, 
    Select, 
    mimeTypeMap, 
    getTimeStamp, 
    boolAndReducer,
    appTitle
} from "src/components/common/functions";

export default function ProfitMargin({ tabTitle }){
    const user = useMyStore(state => state.user);
    //-------------------------------------------------------------------------
    const [updateDateTime, setUpdateDateTime] = useState(getTimeStamp());
    const [showLoading, setShowLoading] = useState(false);
    const [projectsMap, setProjectsMap] = useState({});
    const projects = Object.entries(projectsMap).map(([projectHashKey, projectName]) =>({value: projectHashKey, label: projectName}));
    const [projectHashKey, setProjectHashKey] = useState("");
    const [projectData, setProjectData] = useState([]);
    const [filterWords, setFilterWords] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const projectName = projects.find(project => project.value === projectHashKey)?.projectName ?? "";
    const filteredProjectData = projectData.filter(u => (filterWords.toLowerCase().split(" ").map(f => JSON.stringify(
    Object.values(u)).toLowerCase().indexOf(f) > -1).reduce(boolAndReducer, true)));
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentPageItems = filteredProjectData.slice(indexOfFirstItem, indexOfLastItem);
    const pageNumbers = Array.from({length: Math.ceil(filteredProjectData.length / itemsPerPage)}, (_, i) => (i + 1));
    //-------------------------------------------------------------------------
    const generateExcel = async() => {
        const workbook = new exceljs.Workbook();
        workbook.creator = user.userName;
        workbook.created = new Date();
        workbook.calcProperties.fullCalcOnLoad = true;
        //-------------------------------------------------------------------------------------------
        const worksheet = workbook.addWorksheet();
        worksheet.views = [{showGridLines: false}];
        worksheet.state = "visible";
        worksheet.properties.defaultColWidth = 22.57;
        const rows = filteredProjectData.map(post => [
            post["Locator Name"], 
            post["Job Site Address"], 
            post["Space Number"], 
            post["Leak"],
            post["Notes"]
        ])
        worksheet.addTable({
            name: "MyTable",
            ref: "A1",
            style: {
                showRowStripes: false,
            },
            columns: [
                { name: "Locator Name", filterButton: true},
                { name: "Job Site Address", filterButton: true},
                { name: "Space Number", filterButton: true},
                { name: "Leak", filterButton: true},
                { name: "Notes", filterButton: true},
            ],
            rows: rows,
        });
        worksheet.columns.forEach(column => {
            const minLength = 18;
            let maxLength = 0;
            column["eachCell"]({ includeEmpty: true }, cell => {
                // exclude row 1 - ---------
                if(cell.row === 1) return;
                //-------------------------
                const columnLength = cell.value?
                cell.value.toString().length * (cell.font?cell.font.size / 11 * 1.2:1.3)
                :minLength;
                if(columnLength > maxLength){
                    maxLength = columnLength;
                }
            });
            column.width = maxLength < minLength ? minLength : maxLength;
        });
        workbook.xlsx.writeBuffer().then(buffer => {
            saveAs(new Blob([buffer], { type: mimeTypeMap.xlsx }), `Gas Leak Survey Export For ${projectName}.xlsx`);
        })  
    }
    //-------------------------------------------------------------------------
    useEffect(() => {
        let isMounted = true;
        const getData = async() => {
            const formData = new MyFormData();
            //-------------------------------------
            setShowLoading(true);
            const resProjects = await myAxios("approvals/readProjectNames.php", formData);
            if(!isMounted) return;
            setShowLoading(false);
            if(!resProjects) return;
            //-------------------------------------
            setProjectsMap(resProjects.data);
        }
        getData();
        return () => {isMounted = false;}
    }, [updateDateTime])
    useEffect(() => {
        let isMounted = true;
        if(!projectHashKey) return;
        const getData = async() => {
            const formData = new MyFormData();
            formData.append("projectHashKey", projectHashKey);
            //-------------------------------------
            setShowLoading(true);
            const resData = await myAxios("approvals/readGasLeakSurvey.php", formData);
            if(!isMounted) return;
            setShowLoading(false);
            if(!resData) return;
            //-------------------------------------
            const localData = resData.data
            .map(item => ({...item, forms: JSON.parse(item.forms).filter(f => f.form === "Gas Leak Survey")}))
            .map(ff => ff.forms.filter(fff => fff.formSubmitTime).map(fff => ({
                ...JSON.parse(fff.content || "{}"), 
                projectHashKey: ff.projectHashKey,
                workInstructionHashKey: ff.workInstructionHashKey,
                workLogHashKey: ff.workLogHashKey,
                hashKey: fff.hashKey
            }))).reduce(arrayReducer, []);
            setProjectData(localData);
        }
        getData();
        return () => {isMounted = false;}
    }, [projectHashKey, updateDateTime])
    useEffect(() => {
        document.title = tabTitle;
        return () => {
            document.title = appTitle;
        };
    }, [tabTitle]);
    //-------------------------------------------------------------------------
    return (
    <div>
        <div className = "cssTab">
            <div className = "cssHeader" style = {{background: user.userTheme}}>
                <span>
                    <Resize/>
                    {tabTitle}
                    <span data-toggle = "tooltip" title = {"Refreshed on " + updateDateTime} className = "btn btn-primary py-1 ml-4" onClick = {() => setUpdateDateTime(getTimeStamp())}>↻</span>
                </span>
                <span>
                    <input
                        className = "form-control w-auto"
                        type = "text"
                        value = {filterWords}
                        onChange = {e => {
                            setFilterWords(e.currentTarget.value.toLowerCase());
                            setCurrentPage(1);
                        }}
                    />
                    <img alt = "" src = {magnifierImg}/>
                </span>
                <span className = "pointer" onClick = {() => setProjectHashKey("All")}>All</span>
            </div>
            {<div className = "cssContent pb10">
                {showLoading?<div className = "cssLoading">Loading...</div>:<>
                <div className = "d-flex align-items-center mb-2">
                    <label className = "mb-0">Project</label>
                    <div className = "mx-3 flex-grow-1">
                        <Select
                            value = {projects.find(x => x.value === projectHashKey)}
                            onChange = {e => {
                                setProjectHashKey(e?.value);
                            }}
                            options = {projects}
                        />
                    </div>
                    {filteredProjectData.length?
                    <span
                        className = "btn btn-success"
                        onClick = {generateExcel}
                    >
                        Excel
                    </span>
                    :<></>}
                </div>
                {!filteredProjectData.length?<div className = "cssLoading">Empty</div>:
                <div className = "table-responsive user-select-text">
                        <table className = "table">
                            <thead className = "noTableTopBorder">
                                <tr>
                                    <th>Locator Name</th>
                                    <th>Job Site Address</th>
                                    <th>Space Number</th>
                                    <th>Leak</th>
                                    <th>Notes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentPageItems.map((item, itemIndex) => 
                                <tr key = {itemIndex}>
                                    <td>{item["Locator Name"]}</td>
                                    <td>{item["Job Site Address"]}</td>
                                    <td>{item["Space Number"]}</td>
                                    <td>{item["Leak"]}</td>
                                    <td>{item["Notes"]}</td>
                                    <td className = "h4">
                                        <Link 
                                            to = {
                                                `/Projects/${
                                                    item.projectHashKey
                                                }/WorkInstructions/${
                                                    item.workInstructionHashKey
                                                }/WorkLogs/${
                                                    item.workLogHashKey
                                                }/${
                                                    item.hashKey
                                                }`
                                            }
                                        >
                                            →
                                        </Link>
                                    </td>
                                </tr>
                                )}
                            </tbody>
                        </table>
                </div>
                }
                </>}
                {!showLoading && filteredProjectData.length?
                <Footer
                    length = {filteredProjectData.length}
                    pageNumbers = {pageNumbers}

                    currentPage = {currentPage}
                    setCurrentPage = {setCurrentPage}
                    itemsPerPage = {itemsPerPage}
                    setItemsPerPage = {setItemsPerPage}
                />
                :null}
            </div>}
        </div>    
    </div>
    )
}